import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { supplierService, Supplier } from '../../services/supplierService'

export const SuppliersPage = () => {
  const [search, setSearch] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [page, setPage] = useState(1)

  const { data: suppliersData, isLoading } = useQuery({
    queryKey: ['suppliers', search, countryFilter, page],
    queryFn: () => supplierService.getAll({ 
      search: search || undefined, 
      country: countryFilter || undefined,
      page, 
      limit: 20 
    }),
  })

  const { data: countriesData } = useQuery({
    queryKey: ['supplier-countries'],
    queryFn: () => supplierService.getCountries(),
  })

  const suppliers: Supplier[] = suppliersData?.data?.data?.items || []
  const pagination = suppliersData?.data?.data?.pagination
  const countries = countriesData?.data?.data || []

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your suppliers, currencies, and price lists
          </p>
        </div>
        <Link
          to="/suppliers/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-5 h-5" />
          New Supplier
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search suppliers by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Countries</option>
            {countries.map((c: any) => (
              <option key={c.country} value={c.country}>
                {c.country} ({c.supplier_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Suppliers Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <BuildingOfficeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No suppliers found</h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Try adjusting your search terms' : 'Get started by adding your first supplier'}
          </p>
          {!search && (
            <Link
              to="/suppliers/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              New Supplier
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suppliers.map((supplier) => (
              <Link
                key={supplier.supplier_id}
                to={`/suppliers/${supplier.supplier_id}`}
                className="bg-white rounded-xl shadow border border-gray-100 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-lg">
                    {getInitials(supplier.company_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {supplier.company_name}
                    </h3>
                    {supplier.supplier_code && (
                      <p className="text-xs text-gray-500">{supplier.supplier_code}</p>
                    )}
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>

                <div className="mt-4 space-y-2">
                  {supplier.contact_person && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">Contact:</span>
                      {supplier.contact_person}
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    <span className="truncate">
                      {[supplier.city, supplier.country].filter(Boolean).join(', ') || 'No location'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                    <span>
                      {supplier.currencies?.symbol || supplier.default_currency_code || 'KES'}
                      {' '}
                      {supplier.currencies?.currency_name || 'Kenyan Shilling'}
                    </span>
                  </div>

                  {supplier.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{supplier.email}</span>
                    </div>
                  )}

                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <PhoneIcon className="w-4 h-4 text-gray-400" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {supplier._count?.purchases || 0} purchases
                  </span>
                  {supplier.payment_terms_days ? (
                    <span className="text-gray-500">
                      Net {supplier.payment_terms_days}
                    </span>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} suppliers
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
